import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { environment } from './environments/environment';
import { httpInterceptorProviders } from './app/http-interceptors';
import { KaisatsuErrorHandler } from './app/shared/kaisatsu-error-handler';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { AppRoutingModule } from './app/app-routing.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { SpenFormFieldModule, SpenModule } from '@classi/spen';
import { ReactiveFormsModule } from '@angular/forms';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { AppComponent } from './app/app.component';
import loginChallengeSlice from './app/shared/store/login-challenge.slice';
import globalMessageSlice from './app/shared/store/global-message.slice';
import ssoTeachersSlice from './app/shared/store/sso-teachers.slice';
import ssoStudentsSlice from './app/shared/store/sso-students.slice';
import passwordResetSlice from './app/shared/store/password-reset.slice';

if (environment.production) {
  enableProdMode();
}

/**
 *
 * @see https://mswjs.io/docs/recipes/deferred-mounting
 *
 */

async function prepare() {
  if (environment.kaisatsuApiBaseUrl === '') {
    const { worker } = await import('./mocks/browser');
    return await worker.start();
  }
  return;
}

if (environment.sentry !== null) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.sentry.release,
    integrations: [Sentry.browserTracingIntegration({})],
    tracesSampleRate: 0.001,
    sendDefaultPii: false, // v10でのIP収集制御を明示的に設定
    ignoreErrors: [
      /\bCan't find variable: removeAllHighlightsForSB\b/,
      /\bCan't find variable: MyApp_RemoveAllHighlights\b/,
      /\bCan't find variable: WKWebView_RemoveAllHighlights\b/,
      /\bonSvFinishLoading is not defined\b/,
      'window.onorientationchange is not a function',
      // GAから出てるエラー
      /^L$/,
      /^N$/,
    ],
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });
}

prepare()
  .then(() => {
    return bootstrapApplication(AppComponent, {
      providers: [
        provideZoneChangeDetection(),
        importProvidersFrom(
          BrowserModule,
          AppRoutingModule,
          SpenFormFieldModule,
          SpenModule,
          ReactiveFormsModule,
          // shared store
          StoreModule.forRoot({}),
          StoreModule.forFeature(loginChallengeSlice),
          StoreModule.forFeature(globalMessageSlice),
          StoreModule.forFeature(ssoTeachersSlice),
          StoreModule.forFeature(ssoStudentsSlice),
          StoreModule.forFeature(passwordResetSlice),
          // Instrumentation must be imported after importing StoreModule (config is optional)
          StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
          })
        ),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        httpInterceptorProviders,
        {
          provide: ErrorHandler,
          useClass: KaisatsuErrorHandler,
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
      ],
    });
  })
  .catch((err) => console.error(err));
